import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Sparkles, Loader2, ImageUp, AlertCircle, X } from 'lucide-react';
import { LANGUAGES, DEFAULT_LANGUAGE } from './LanguageSelect';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCEPT = ['image/jpeg', 'image/png', 'image/webp'];

// Fills only empty fields; idioma counts as empty while still the default
const merge = (form, ai) => {
  const next = { ...form };
  const empty = (k) => !String(form[k] || '').trim();
  ['titulo', 'editora', 'isbn', 'edicao', 'ano_publicacao', 'categoria', 'descricao'].forEach((k) => { if (empty(k) && ai[k]) next[k] = ai[k]; });
  if (empty('autor') && ai.autores?.length) next.autor = ai.autores.join('; ');
  if ((empty('idioma') || form.idioma === DEFAULT_LANGUAGE) && ai.idioma) next.idioma = ai.idioma;
  return next;
};

export const FichaAutofill = ({ setForm, categories, authHeaders }) => {
  const input = useRef(null);
  const [image, setImage] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [drag, setDrag] = useState(false);

  const handle = async (f) => {
    if (!f) return;
    if (!ACCEPT.includes(f.type)) { setStatus('error'); setError('Envie apenas imagem (JPG, PNG ou WebP) da ficha catalográfica.'); return; }
    setImage({ name: f.name, url: URL.createObjectURL(f) });
    setStatus('ai'); setError('');
    const body = new FormData();
    body.append('file', f);
    body.append('languages', JSON.stringify(LANGUAGES));
    body.append('categories', JSON.stringify(categories));
    try {
      const { data } = await axios.post(`${API}/digital/books/extract-metadata-image`, body, { headers: authHeaders });
      setForm((prev) => merge(prev, data));
      setStatus('done');
    } catch (e) {
      setStatus('error');
      setError(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Não foi possível ler a ficha. Preencha os campos manualmente.');
    }
  };

  const busy = status === 'ai';
  const drop = (e) => { e.preventDefault(); setDrag(false); if (!busy) handle(e.dataTransfer.files[0]); };
  return (
    <div data-testid="ficha-autofill" style={{ gridColumn: '1 / -1' }}>
      <p style={{ fontWeight: 600, color: '#2D3748', fontSize: '0.92rem', marginBottom: 4 }}>Ficha catalográfica (preenchimento automático)</p>
      <div role="button" tabIndex={0} data-testid="ficha-dropzone" onClick={() => !busy && input.current.click()}
        onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !busy) input.current.click(); }}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={drop}
        style={{ display: 'flex', alignItems: 'center', gap: '1rem', border: `2px dashed ${drag ? '#2c7a7b' : '#68AAB0'}`, background: drag ? '#e6f6f6' : '#f3fafa',
          borderRadius: 10, padding: '1rem 1.1rem', cursor: busy ? 'wait' : 'pointer', transition: 'background-color 0.15s ease, border-color 0.15s ease' }}>
        {image ? <img src={image.url} alt="Ficha enviada" data-testid="ficha-preview" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, border: '1px solid #e2e8f0' }} />
          : <ImageUp className="w-9 h-9" style={{ color: '#2c7a7b', flexShrink: 0 }} />}
        <div style={{ minWidth: 0 }}>
          <p style={{ fontSize: '0.88rem', color: '#2D3748', fontWeight: 600 }}>{image ? image.name : 'Arraste a imagem da ficha aqui ou clique para escolher'}</p>
          <p style={{ fontSize: '0.78rem', color: '#718096' }}>JPG, PNG ou WebP. A IA lê a ficha e preenche os campos vazios; depois é só conferir.</p>
        </div>
        {image && !busy && (
          <button type="button" onClick={(e) => { e.stopPropagation(); setImage(null); setStatus(null); }} data-testid="ficha-clear-btn" title="Remover imagem"
            style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#a0aec0', cursor: 'pointer' }}><X className="w-4 h-4" /></button>
        )}
      </div>
      <input ref={input} type="file" accept={ACCEPT.join(',')} hidden data-testid="ficha-file-input" onChange={(e) => { handle(e.target.files[0]); e.target.value = ''; }} />
      {busy && <p data-testid="ficha-autofill-loading" style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#2c7a7b', fontSize: '0.85rem', marginTop: 8 }}><Loader2 className="w-4 h-4 animate-spin" /> A IA está lendo a ficha...</p>}
      {status === 'done' && <p data-testid="ficha-autofill-done" style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#276749', fontSize: '0.85rem', marginTop: 8 }}><Sparkles className="w-4 h-4" /> Campos preenchidos pela IA. Confira tudo antes de salvar.</p>}
      {status === 'error' && <p data-testid="ficha-autofill-error" style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#c53030', fontSize: '0.85rem', marginTop: 8 }}><AlertCircle className="w-4 h-4" /> {error}</p>}
    </div>
  );
};
